/**
 * Debug Settings — manual visual test bench for every settings input type.
 *
 * Flip DEBUG_SETTINGS_ENABLED to `true` and rebuild to surface a "Debug"
 * tab in the Settings modal that exercises every control type. The const-gated
 * include in `sharedSettingsConfig.ts` is dead-code-eliminated when the flag is
 * `false`, so this file is tree-shaken from production builds.
 *
 * Note: keys are namespaced with `_dbg_*` and are NOT typed against UserSettings.
 * They round-trip through the same persistence layer (localStorage on web, the
 * user_config.json file on Electron) as harmless extra keys; nothing in the app
 * reads them.
 */

import { SettingConfig, UserSettingConfig } from '../types/settingsTypes';

export const DEBUG_SETTINGS_ENABLED = false;

const DEBUG_CATEGORY = 'Debug';

const asDebugSetting = <T extends Omit<SettingConfig, 'category'>>(
	s: T,
): UserSettingConfig =>
	({
		...s,
		category: DEBUG_CATEGORY,
		isClientConfig: true,
		editable: true,
		showReset: true,
	}) as UserSettingConfig;

export const debugClientSettings: Record<string, UserSettingConfig[]> = {
	[DEBUG_CATEGORY]: [
		asDebugSetting({
			key: '_dbg_text',
			label: 'Text',
			description: 'Plain text input',
			controlType: 'text',
			type: 'string',
			default: 'Hello world',
		}),
		asDebugSetting({
			key: '_dbg_text_long',
			label: 'Text (long → popover)',
			description:
				'Values longer than ~35 chars switch to a read-only preview with a popover editor',
			controlType: 'text',
			type: 'string',
			default:
				'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod.',
			tokenFlag: true,
		}),
		asDebugSetting({
			key: '_dbg_select',
			label: 'Select (static)',
			description:
				'Static dropdown; last option is intentionally long to test overflow',
			controlType: 'select',
			optionsSource: 'static',
			options: [
				{ value: 'red', label: 'Red' },
				{ value: 'green', label: 'Green' },
				{ value: 'blue', label: 'Blue' },
				{
					value: 'long',
					label: 'A deliberately long option label to test overflow handling in the dropdown',
				},
			],
			type: 'string',
			default: 'red',
		}),
		asDebugSetting({
			key: '_dbg_multiselect',
			label: 'Multi-select',
			description:
				'Toggleable button tags; value is the array of selected option strings',
			controlType: 'multiSelect',
			options: ['Alpha', 'Bravo', 'Charlie', 'Delta', 'Echo'],
			type: 'list',
			default: ['Alpha', 'Charlie'],
		}),
		asDebugSetting({
			key: '_dbg_switch',
			label: 'Switch',
			description: 'Two-button binary toggle',
			controlType: 'switch',
			optionA: { value: 'on', label: 'On' },
			optionB: { value: 'off', label: 'Off' },
			type: 'bool',
			default: 'off',
		}),
		asDebugSetting({
			key: '_dbg_checkbox',
			label: 'Checkbox',
			description: 'Single boolean toggle',
			controlType: 'checkbox',
			type: 'bool',
			default: false,
		}),
		asDebugSetting({
			key: '_dbg_numberinput',
			label: 'Number input',
			description: 'Range 0-100, integer step',
			controlType: 'numberInput',
			constraints: { min: 0, max: 100, step: 1, unit: 'px' },
			type: 'number',
			default: 42,
		}),
		asDebugSetting({
			key: '_dbg_dualinput_fractional',
			label: 'Dual input (0-1, step 0.01)',
			description:
				'Slider + number with fractional step — exercises decimal width',
			controlType: 'dualInput',
			constraints: { min: 0, max: 1, step: 0.01 },
			type: 'number',
			default: 0.5,
		}),
		asDebugSetting({
			key: '_dbg_dualinput_large',
			label: 'Dual input (0-131072)',
			description:
				'n_ctx-class range — exercises the MAX_FIELD_CHARS cap on the number field',
			controlType: 'dualInput',
			constraints: { min: 0, max: 131072, step: 1, unit: 'tok' },
			type: 'number',
			default: 4096,
		}),
		asDebugSetting({
			key: '_dbg_dualinput_signed',
			label: 'Dual input (-50 to 50)',
			description: 'Signed range — exercises sign char in width calc',
			controlType: 'dualInput',
			constraints: { min: -50, max: 50, step: 1 },
			type: 'number',
			default: 0,
		}),
		asDebugSetting({
			key: '_dbg_multicheckbox_flat',
			label: 'Multi-checkbox (flat)',
			description: 'Object value with no groups → renders flat',
			layout: 'stacked',
			controlType: 'multiCheckbox',
			checkboxOptions: [
				{ key: 'foo', label: 'Foo', default: true },
				{ key: 'bar', label: 'Bar', default: false },
				{ key: 'baz', label: 'Baz', default: true },
			],
			type: 'object',
			default: { foo: true, bar: false, baz: true },
		}),
		asDebugSetting({
			key: '_dbg_multicheckbox_grouped',
			label: 'Multi-checkbox (grouped)',
			description:
				'Object value with `group` set → renders collapsible accordion sections',
			layout: 'stacked',
			controlType: 'multiCheckbox',
			checkboxOptions: [
				{
					key: 'a1',
					label: 'Alpha 1',
					default: true,
					group: 'Alpha',
					tooltip: 'Alpha 1 tooltip',
				},
				{ key: 'a2', label: 'Alpha 2', default: false, group: 'Alpha' },
				{ key: 'b1', label: 'Beta 1', default: true, group: 'Beta' },
				{ key: 'b2', label: 'Beta 2', default: false, group: 'Beta' },
				{ key: 'g1', label: 'Gamma 1', default: false, group: 'Gamma' },
			],
			type: 'object',
			default: { a1: true, a2: false, b1: true, b2: false, g1: false },
		}),
		asDebugSetting({
			key: '_dbg_directorypicker',
			label: 'Directory picker',
			description: 'Native dialog (Electron only — does nothing on web)',
			controlType: 'directoryPicker',
			type: 'string',
			default: '',
		}),
		asDebugSetting({
			key: '_dbg_secureinput',
			label: 'Secure input',
			description: 'Password-style input with eye toggle',
			controlType: 'secureInput',
			type: 'string',
			default: '',
		}),
		asDebugSetting({
			key: '_dbg_listtextbox',
			label: 'List textbox',
			description: 'Inline summary; popover editor with one row per item',
			controlType: 'textbox',
			type: 'list',
			default: ['one', 'two', 'three'],
		}),
		{
			...asDebugSetting({
				key: '_dbg_info',
				label: 'Info',
				description: 'Read-only display for computed/system values',
				controlType: 'info',
				type: 'string',
				default: 'Read-only value displayed here',
			}),
			editable: false,
			showReset: false,
		},
		{
			...asDebugSetting({
				key: '_dbg_infoaction',
				label: 'Info action',
				description: 'Action button — debug entry is a no-op',
				controlType: 'infoAction',
				type: 'string',
				default: '',
				actionLabel: 'Run Action',
				actionIcon: 'fas fa-play',
				loadingLabel: 'Running…',
			}),
			showReset: false,
		},
	],
};
